import { assign as _assign, get as _get } from 'lodash-es';

/**
 * This object encapsulates the interaction with the Compatio API
 */
const CompatioApi = {
  /**
   * An object that stores a cache of all requests to retrieve data from the Compatio API.
   * The reason we're doing this is because a user could trigger an API call multiple times before the
   * initial request is returned from the server, so by caching the initial request for the product data,
   * we can avoid additional requests
   */
  categoryRetrievalPromiseCache: {},

  targetGraphApiEndpoint: [
    {
      Region: 'Live',
      Url: 'https://9h6rg5qonl.execute-api.us-east-2.amazonaws.com/prod-live',
    },
    {
      Region: 'LiveV2',
      // Url: 'https://9h6rg5qonl.execute-api.us-east-2.amazonaws.com/prod-live',
      Url: 'https://xrs.compat.io',
    },
    {
      Region: 'LiveV2-api',
      // Url: 'https://9h6rg5qonl.execute-api.us-east-2.amazonaws.com/prod-live',
      Url: 'https://xrs.compat.io',
    },
    {
      Region: 'Master',
      Url: 'https://xrs.compat.io',
      // Url: 'https://70ykngvlsg.execute-api.us-east-2.amazonaws.com/prod-master',
    },
    {
      Region: 'MasterV2Dev',
      Url: 'https://xrs-dev.compat.io',
      // Url: 'https://70ykngvlsg.execute-api.us-east-2.amazonaws.com/prod-master',
    },
    {
      Region: 'MasterV2',
      Url: 'https://70ykngvlsg.execute-api.us-east-2.amazonaws.com/prod-master',
    },
    {
      Region: 'Dev',
      Url: 'https://8xj2musshl.execute-api.us-east-2.amazonaws.com/compatioxrs',
    },
    {
      Region: 'QA',
      Url: 'https://m6a9hz8t5d.execute-api.us-east-2.amazonaws.com/qa',
    },
    {
      Region: 'Stage',
      Url:
        'https://0m933h8vv8.execute-api.us-east-2.amazonaws.com/default/xrs-api',
    },
    { Region: 'Preview', Url: '' },
  ],
  // TODO: Update this to the final API url (some subdomain of compat.io)
  apiEndpoint:
    'https://1ciuemjzva.execute-api.us-east-2.amazonaws.com/compatio_xrs',

  shopifyApiEndpointProd:
    'https://9h6rg5qonl.execute-api.us-east-2.amazonaws.com/prod-live',

  shopifyApiEndpointQA:
    'https://m6a9hz8t5d.execute-api.us-east-2.amazonaws.com/qa',

  /**
   * Reset object to its initial state. Useful for automated testing.
   */
  reset: function() {
    this.categoryRetrievalPromiseCache = {};
  },

  /**
   * Retrieve Compatio compatibility data for a specific merchantProductSku
   *
   * @param merchantProductSku
   * @returns {Promise<any>}
   */
  getCategoryDataByProductSku: async function(
    merchantProductSku,
    maximumCategories,
    maximumProducts
  ) {
    if (this.categoryRetrievalPromiseCache.hasOwnProperty(merchantProductSku)) {
      return this.categoryRetrievalPromiseCache[merchantProductSku];
    }

    const retrieveCategoryDataPromise = await this._getCategoryData(
      merchantProductSku,
      maximumCategories,
      maximumProducts
    );

    // Store Promise in case this exact category ID is requested before initial API call resolves
    this.categoryRetrievalPromiseCache[
      merchantProductSku
    ] = retrieveCategoryDataPromise;

    return retrieveCategoryDataPromise;
  },

  /**
   * Return a Promise that will return the category data
   * @param merchantProductSku
   * @returns {Promise<any>}
   * @private
   */
  async _getCategoryData(
    merchantProductSku,
    maximumCategories = 5,
    maximumProducts = 150
  ) {
    const locale = window.compatioConfig.locale;
    const IsPlatformShopify =
      typeof _get(window, 'compatioConfig.shopify') === 'object'
        ? 'shopify'
        : 'magento';
    const apiUpdate = _get(window, 'compatioConfig.apiUpdate');
    const authorization = `JWT ${window.compatioConfig.compatioApiKey}`;
    let merchantKey = '';
    let fullApiEndpoint = '';
    let targetGraph = '';
    if (IsPlatformShopify == 'shopify') {
      merchantKey = _get(window, 'compatioConfig.shopify.merchantKey')
        ? _get(window, 'compatioConfig.shopify.merchantKey')
        : _get(window, 'compatioConfig.magento.merchantKey');
      targetGraph = _get(window, 'compatioConfig.shopify.targetGraph')
        ? _get(window, 'compatioConfig.shopify.targetGraph')
        : _get(window, 'compatioConfig.magento.targetGraph');
      if (apiUpdate == true) {
        fullApiEndpoint = `${
          this.targetGraphApiEndpoint.find(o => o.Region === targetGraph).Url
        }?merchantSku=${merchantProductSku}&merchantKey=${merchantKey}&eCommercePlatform=shopify&maximumCategories=${maximumCategories}&productsPerCategory=${maximumProducts}`;
      } else {
        fullApiEndpoint = `${
          this.targetGraphApiEndpoint.find(o => o.Region === targetGraph).Url
        }?variantID=${merchantProductSku}&merchantKey=${merchantKey}&eCommercePlatform=shopify&maximumCategories=${maximumCategories}&productsPerCategory=${maximumProducts}`;
      }
    } else if (IsPlatformShopify == 'magento') {
      merchantKey = _get(window, 'compatioConfig.shopify.merchantKey')
        ? _get(window, 'compatioConfig.shopify.merchantKey')
        : _get(window, 'compatioConfig.magento.merchantKey');
      targetGraph = _get(window, 'compatioConfig.shopify.targetGraph')
        ? _get(window, 'compatioConfig.shopify.targetGraph')
        : _get(window, 'compatioConfig.magento.targetGraph');
      fullApiEndpoint = `${
        this.targetGraphApiEndpoint.find(o => o.Region === targetGraph).Url
      }?variantID=${merchantProductSku}&merchantKey=${merchantKey}&eCommercePlatform=magento&maximumCategories=${maximumCategories}&productsPerCategory=${maximumProducts}`;
    } else {
      fullApiEndpoint = `${this.apiEndpoint}?gmsProductID=${merchantProductSku}&locale=${locale}`;
    }
    // alert(`${
    //   this.targetGraphApiEndpoint.find(o => o.Region === targetGraph).Url
    // }`)
    return await fetch(fullApiEndpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(function(response) {
        if (!response.ok) {
          //throw new Error('HTTP error, status = ' + response.status);
          return [];
        }
        return response.json();
      })
      .then(json => {
        if (!json.hasOwnProperty('compatibleCategories')) {
          throw new Error('Malformed data, response = ' + json);
        }
        // TODO: Remove this once the API returns this key for all categories
        json.compatibleCategories.map(item => {
          if (!item.hasOwnProperty('categoryId')) {
            item.categoryId = item.categoryID;
          }
          return item;
        });
        return json.compatibleCategories;
      })
      .catch(err => {
        //console.log(err)
        return [];
      });
  },

  /**
   * Extract unique product IDs from all categories from Compatio category data
   *
   * @param compatioCategoryData
   * @returns {any[]}
   */
  extractAllProductSkus: function(compatioCategoryData) {
    const productSkus = compatioCategoryData.flatMap(category => {
      return category.products.map(product => {
        return product.merchantProductId;
      });
    });
    // Return unique product IDs
    return [...new Set(productSkus)];
  },

  /**
   * Extract unique product IDs from a specific Compatio category
   *
   * @param compatioCategoryData
   * @param categoryId
   * @returns {any[]}
   */
  extractProductSkusByCategoryId: function(compatioCategoryData, categoryId) {
    const selectedCategory = compatioCategoryData.find(categoryObject => {
      return categoryObject.categoryId === categoryId;
    });
    const productSkus = selectedCategory.products.map(product => {
      return product.merchantProductId;
    });

    // Return unique product IDs
    return [...new Set(productSkus)];
  },

  /**
   * Merge Compatio product data with Ecommerce product data
   *
   * This allows Compatio to pass back:
   * 1. Sorting data
   * 2. "extraData" that will be displayed to the user
   *
   * @param selectedProductData
   * @param compatioCategoryData
   * @param categoryId
   * @returns {*}
   */
  mergeCompatioDataWithProductData: function(
    selectedProductData,
    compatioCategoryData,
    categoryId
  ) {
    const selectedCategory = compatioCategoryData.find(categoryObject => {
      return categoryObject.categoryId === categoryId;
    });

    const compatioProductData = selectedCategory.products;

    const mergedProductData = selectedProductData.map(product => {
      const matchingCompatioProduct = compatioProductData.find(
        compatioProduct => {
          return (
            compatioProduct.merchantProductId === product.merchantProductSku
          );
        }
      );

      if (matchingCompatioProduct) {
        // Merge Ecommerce + Compatio data
        _assign(product, matchingCompatioProduct);
      }
      return product;
    });
    return mergedProductData;
  },
};

// export to the global namespace to allow methods to be overridden from code that doesn't import this class directly
window.CompatioApi = CompatioApi;

export default CompatioApi;
